<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useListStoreNew } from "@/stores/ListStoreNew";
import { usePagination } from "@/composables/usePagination";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import apiClient from "@/apiClient.js";
import RequisitionForm from "./RequisitionForm.vue";
import dayjs from "dayjs";
import { PlusCircleIcon } from "@heroicons/vue/24/solid";
import { useAuthStore } from "@/stores/AuthStore";
import { usePresentationMode } from "@/composables/usePresentationMode";

const list = useListStoreNew();
const auth = useAuthStore();
const router = useRouter();
const { maskPoundAmount } = usePresentationMode();
const searchTerm = ref("");
const perPageOptions = [25, 50, 100];
const perPage = ref(25);
const categories = ref([]);
const suppliers = ref([]);
const supplierFilter = ref("");
const categoryFilter = ref("");
const createOpen = ref(false);
const creating = ref(false);
const createFiles = ref([]);
const createFileUploading = ref(false);
const createFileInputKey = ref(0);
const canManageAll = computed(
  () => auth.hasAnyRole(["finance-audit", "finance-audits", "super"]),
);
const canViewFinancials = computed(() => auth.hasFinanceRole());
const columnCount = computed(() => (canViewFinancials.value ? 7 : 6));

const emptyCreateForm = () => ({
  item: "",
  description: "",
  purpose: "",
  information: "",
  supplier_id: "",
  category_id: "",
  cost_ex_vat: "",
  approved_to_order_at: "",
  payment_method: "",
  payment_issued_at: "",
});

const createForm = reactive(emptyCreateForm());

const defaultCategoryFromSupplier = (supplierId) => {
  if (!supplierId) {
    return "";
  }
  const supplier = suppliers.value.find(
    (entry) => Number(entry.id) === Number(supplierId),
  );
  return supplier?.category_id ?? supplier?.category?.id ?? "";
};

watch(
  () => createForm.supplier_id,
  (supplierId) => {
    createForm.category_id = defaultCategoryFromSupplier(supplierId);
  },
);


const { setUrl, changePage } = usePagination(list, null, () => ({
  per_page: perPage.value,
  search: searchTerm.value || undefined,
  supplier_id: supplierFilter.value || undefined,
  category_id: categoryFilter.value || undefined,
}));

const loadFilters = async () => {
  const [{ data: supplierData }, { data: categoryData }] = await Promise.all([
    apiClient.get("/api/supplier?page=all"),
    apiClient.get("/api/supplier/categories"),
  ]);
  suppliers.value = supplierData?.data ?? supplierData ?? [];
  categories.value = categoryData;
};

const applyFilters = () => {
  list.clearFilters();
  if (searchTerm.value) {
    list.addFilter("search", encodeURIComponent(searchTerm.value));
  }
  if (supplierFilter.value) {
    list.addFilter("supplier_id", supplierFilter.value);
  }
  if (categoryFilter.value) {
    list.addFilter("category_id", categoryFilter.value);
  }
  list.addFilter("per_page", perPage.value);
};

const runSearch = () => {
  applyFilters();
  list.resetPage();
  setUrl();
};

const resetFilters = () => {
  searchTerm.value = "";
  supplierFilter.value = "";
  categoryFilter.value = "";
  runSearch();
};

const toPence = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : Math.round(parsed * 100);
};

const openCreateModal = () => {
  Object.assign(createForm, emptyCreateForm());
  createFiles.value = [];
  createFileInputKey.value += 1;
  createOpen.value = true;
};

const closeCreateModal = () => {
  createOpen.value = false;
  createFiles.value = [];
  createFileInputKey.value += 1;
};

const handleCreateFiles = (event) => {
  createFiles.value = Array.from(event.target.files || []).filter(Boolean);
};

const saveNewRequisition = async () => {
  if (!createForm.supplier_id || !createForm.category_id) {
    alert("Please select a supplier and category.");
    return;
  }
  if (!createForm.purpose?.trim()) {
    alert("Purpose is required.");
    return;
  }
  if (canViewFinancials.value && (createForm.cost_ex_vat === "" || createForm.cost_ex_vat === null)) {
    alert("Cost (ex VAT) is required.");
    return;
  }

  creating.value = true;
  try {
    const { data } = await apiClient.post("/api/requisition", {
      ...createForm,
      cost_ex_vat: canViewFinancials.value ? toPence(createForm.cost_ex_vat) : null,
    });
    const requisitionId = data?.data?.id ?? data?.id;
    if (requisitionId && createFiles.value.length) {
      createFileUploading.value = true;
      const formData = new FormData();
      createFiles.value.forEach((file) => {
        formData.append("files[]", file);
      });
      await apiClient.post(
        `/api/requisition/${requisitionId}/files`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    }
    closeCreateModal();
    list.fetchIndex();
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to create requisition.");
  } finally {
    createFileUploading.value = false;
    creating.value = false;
  }
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) {
    return maskPoundAmount("£0.00");
  }
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(value / 100);
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }
  return dayjs(value).format("DD MMM YYYY");
};

const initialsFor = (name) => {
  const parts = (name || "").trim().split(" ").filter(Boolean);
  if (!parts.length) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const openRequisition = (row, event) => {
  if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
    return;
  }

  router.push({ name: "requisition-view", params: { id: row.id } });
};

const openRequisitionInNewTab = (event, row) => {
  if (event.button === 1) {
    window.open(`/requisitions/${row.id}`, "_blank");
  }
};

const approvalStatus = (row) => {
  if (row.declined_at) {
    return {
      label: "Declined",
      class: "bg-rose-100 text-rose-700",
    };
  }

  if (!row.approved_to_order_at) {
    return {
      label: "Pending",
      class: "bg-amber-100 text-amber-700",
    };
  }

  return {
    label: "Approved",
    class: "bg-emerald-100 text-emerald-700",
    date: formatDate(row.approved_to_order_at),
    approver: row?.approved_to_order_by?.name ?? null,
  };
};

onMounted(async () => {
  list.configure({ remote: "/api/requisition", local: "/requisitions" });
  loadFilters();
  applyFilters();
  list.fetchIndex();
});
</script>

<template>
  <div class="space-y-4">
    <form-box header="Requisitions">
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-12">
        <input
          v-model="searchTerm"
          class="input min-w-0 px-4 py-2 sm:col-span-2 lg:col-span-4"
          placeholder="Search item, description or purpose"
          @keyup.enter="runSearch"
        >
        <select v-model="supplierFilter" class="input min-w-0 lg:col-span-2">
          <option value="">All Suppliers</option>
          <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">{{ supplier.company }}</option>
        </select>
        <select v-model="categoryFilter" class="input min-w-0 lg:col-span-2">
          <option value="">All Categories</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
        </select>
        <select v-model.number="perPage" class="input min-w-0 lg:col-span-2">
          <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }} / page</option>
        </select>

        <div class="grid grid-cols-2 gap-2 sm:col-span-2 lg:col-span-2">
          <button class="btn w-full px-3 py-2 text-sm" @click.prevent="runSearch">
            Search
          </button>
          <button class="btn w-full px-3 py-2 text-sm" @click.prevent="resetFilters">
            Clear
          </button>
        </div>

        <button
          class="btn flex w-full items-center justify-center gap-2 px-3 py-2 text-sm sm:col-span-2 lg:col-span-3 lg:justify-self-end"
          @click.prevent="openCreateModal"
        >
          <PlusCircleIcon class="h-4 w-4 shrink-0" />
          <span>New Requisition</span>
        </button>
      </div>
    </form-box>

    <template v-if="!list.isLoading">
      <div class="space-y-3 lg:hidden">
        <button
          v-for="row in list.getIndex"
          :key="row.id"
          type="button"
          class="box !mb-0 !p-4 text-left"
          @click="event => openRequisition(row, event)"
          @auxclick="event => openRequisitionInNewTab(event, row)"
        >
          <div class="flex min-w-0 items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {{ row.item ?? "—" }}
              </p>
              <p class="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                {{ row.supplier?.company ?? "—" }}
              </p>
            </div>
            <span
              v-if="canViewFinancials"
              class="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-100"
            >
              {{ formatCurrency(row.cost_ex_vat) }}
            </span>
          </div>

          <p class="mt-2 text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
            {{ row.purpose || row.description || "No details provided" }}
          </p>

          <div class="mt-3 flex min-w-0 items-center justify-between gap-2">
            <div class="flex min-w-0 items-center gap-2">
              <div
                class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 aspect-square"
              >
                <img
                  v-if="row.requested_by?.avatar_url"
                  :src="row.requested_by.avatar_url"
                  :alt="row.requested_by?.name"
                  class="block h-full w-full object-cover object-center"
                />
                <span v-else>{{ initialsFor(row.requested_by?.name) }}</span>
              </div>
              <span class="truncate text-xs text-slate-600 dark:text-slate-300">
                {{ row.requested_by?.name ?? "Unknown" }}
              </span>
            </div>

            <span
              :class="[
                'inline-flex shrink-0 items-center rounded-full px-2 py-1 text-[11px] font-semibold',
                approvalStatus(row).class,
              ]"
            >
              {{ approvalStatus(row).label }}
            </span>
          </div>
        </button>

        <div v-if="list.getIndex.length < 1" class="box text-sm text-slate-500 dark:text-slate-300">
          No requisitions yet.
        </div>

        <div class="box !mb-0">
          <PrevNextButtons :showPrev="list.showPrev" :showNext="list.showNext" @prev="changePage(-1)" @next="changePage(1)" />
        </div>
      </div>

      <table class="box list hidden lg:table">
        <thead>
          <tr class="text-left text-xs uppercase text-slate-500">
            <th class="px-3 py-2 hidden lg:table-cell">Requested By</th>
            <th class="px-3 py-2">Item</th>
            <th class="px-3 py-2">Supplier</th>
            <th class="px-3 py-2">Purpose</th>
            <th v-if="canViewFinancials" class="px-3 py-2 text-right">Cost</th>
            <th class="px-3 py-2 hidden lg:table-cell">Approved</th>
            <th class="px-3 py-2 hidden lg:table-cell">Payment</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in list.getIndex" :key="row.id"
            class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
            @click="event => openRequisition(row, event)"
            @auxclick="event => openRequisitionInNewTab(event, row)"
          >
            <td class="px-3 py-3 hidden lg:table-cell">
              <div class="flex items-center gap-2">
                <div
                  class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 aspect-square"
                >
                  <img
                    v-if="row.requested_by?.avatar_url"
                    :src="row.requested_by.avatar_url"
                    :alt="row.requested_by?.name"
                    class="block h-full w-full object-cover object-center"
                  />
                  <span v-else>{{ initialsFor(row.requested_by?.name) }}</span>
                </div>
                <span class="text-sm text-slate-700 dark:text-slate-200">
                  {{ row.requested_by?.name ?? "Unknown" }}
                </span>
              </div>
            </td>
            <td class="px-3 py-3">
              <div class="font-semibold">{{ row.item ?? '—' }}</div>
              <div class="text-xs text-slate-500">{{ row.description }}</div>
            </td>
            <td class="px-3 py-3">
              {{ row.supplier?.company ?? '—' }}
            </td>
            <td class="px-3 py-3 hidden md:table-cell">
              {{ row.purpose }}
            </td>
            <td v-if="canViewFinancials" class="px-3 py-3 text-right">
              {{ formatCurrency(row.cost_ex_vat) }}
            </td>
            <td class="px-3 py-3 hidden lg:table-cell w-48">
              <span
                :class="['inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold', approvalStatus(row).class]">
                {{ approvalStatus(row).label }}
              </span>
            </td>
            <td class="px-3 py-3 hidden lg:table-cell">
              <div>{{ row.payment_method ?? '—' }}</div>
              <div class="text-xs text-slate-500">{{ formatDate(row.payment_issued_at) }}</div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td :colspan="columnCount" class="px-3 py-4" v-if="list.getIndex.length < 1">
              No requisitions yet.
            </td>
          </tr>
          <tr>
            <th :colspan="columnCount" class="px-3 py-4">
              <PrevNextButtons :showPrev="list.showPrev" :showNext="list.showNext" @prev="changePage(-1)"
                @next="changePage(1)" />
            </th>
          </tr>
        </tfoot>
      </table>
    </template>

    <template v-else>
      <div class="space-y-3 lg:hidden">
        <div v-for="n in 4" :key="n" class="box !mb-0 animate-pulse">
          <div class="h-4 w-1/2 rounded bg-slate-200 mb-2"></div>
          <div class="h-3 w-1/3 rounded bg-slate-200 mb-3"></div>
          <div class="h-3 w-full rounded bg-slate-200"></div>
        </div>
      </div>

      <table class="box list hidden lg:table">
        <tbody>
          <tr v-for="n in 6" :key="n" class="border-b border-gray-300 animate-pulse">
            <td class="p-3">
              <div class="h-4 bg-slate-200 rounded w-48 mb-2"></div>
              <div class="h-4 bg-slate-200 rounded w-32"></div>
            </td>
            <td class="p-3 hidden lg:table-cell">
              <div class="h-4 bg-slate-200 rounded w-24"></div>
            </td>
            <td class="p-3 hidden md:table-cell">
              <div class="h-4 bg-slate-200 rounded w-32"></div>
            </td>
            <td class="p-3 hidden lg:table-cell">
              <div class="h-4 bg-slate-200 rounded w-24"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <div v-if="createOpen" class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 backdrop-blur p-4">
      <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl">
        <div class="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div>
            <h2 class="text-xl font-semibold">Create Requisition</h2>
            <p class="text-sm text-slate-500 dark:text-slate-300">
              Capture the details and submit for approval.
            </p>
          </div>
          <button class="btn" @click.prevent="closeCreateModal">
            Close
          </button>
        </div>

        <div class="px-6 py-6 max-h-[80vh] overflow-y-auto">
          <RequisitionForm
            :modelValue="createForm"
            :suppliers="suppliers"
            :categories="categories"
            :showFinanceFields="canViewFinancials"
            @update:modelValue="value => Object.assign(createForm, value)"
          />
          <div class="mt-6 space-y-3">
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Files</label>
            <div
              class="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center dark:border-slate-600 dark:bg-slate-900"
            >
              <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Upload supporting documents
              </p>
              <p class="mt-1 text-xs text-slate-500 dark:text-slate-300">
                Add one or more files for this requisition.
              </p>
              <input
                :key="createFileInputKey"
                type="file"
                multiple
                class="mt-4 block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800 dark:text-slate-300"
                @change="handleCreateFiles"
                :disabled="creating || createFileUploading"
              />
            </div>
            <div v-if="createFiles.length" class="text-xs text-slate-500 dark:text-slate-300">
              {{ createFiles.length }} file{{ createFiles.length === 1 ? "" : "s" }} selected
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-8">
            <button class="btn" @click.prevent="closeCreateModal">
              Cancel
            </button>
            <button class="btn" @click.prevent="saveNewRequisition" :disabled="creating || createFileUploading">
              {{ creating || createFileUploading ? "Saving…" : "Create Requisition" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
